import { useState } from "react";
import { useDropzone } from "react-dropzone";
import {
    Box,
    Button,
    FormControlLabel,
    IconButton,
    LinearProgress,
    MenuItem,
    Paper,
    Radio,
    TextField,
    Typography,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { AutoAwesome as GenerateIcon, Close as RemoveIcon, CloudUpload as UploadIcon } from "@mui/icons-material";
import type { DocumentRole, GenerateRequest, GeneratorDocument, PlaybookSummary } from "../../services/lensService";
import { isValidKey, readFileBase64, toKey } from "./lensUtils";

const ROLES: DocumentRole[] = ["template", "guidance", "checklist", "other"];
const MIN_PASTED_LENGTH = 200;

const useStyles = makeStyles()((theme) => ({
    root: { padding: theme.spacing(4), marginBottom: theme.spacing(4) },
    field: { marginBottom: theme.spacing(4) },
    zone: {
        border: "1px dashed #bbb",
        borderRadius: 8,
        padding: theme.spacing(6),
        textAlign: "center",
        cursor: "pointer",
        color: "#555",
        "&:hover": { borderColor: "#7a5cf0" },
    },
    zoneActive: { borderColor: "#7a5cf0", backgroundColor: "#f6f3ff" },
    fileRow: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(2),
        padding: theme.spacing(2),
        marginTop: theme.spacing(2),
    },
    fileName: { flexGrow: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    actions: { display: "flex", gap: theme.spacing(2), flexWrap: "wrap", alignItems: "center", marginTop: theme.spacing(4) },
}));

interface QueuedFile {
    file: File;
    role: DocumentRole;
}

interface PlaybookGeneratorProps {
    existing: PlaybookSummary[];
    // The request is sent and the playbook page opens as soon as the playbook exists.
    starting: boolean;
    isCreating: boolean;
    onGenerate: (request: GenerateRequest) => void;
    onCreateEmpty: (key: string, displayName: string) => void;
    onCancel: () => void;
}

const PlaybookGenerator = ({ existing, starting, isCreating, onGenerate, onCreateEmpty, onCancel }: PlaybookGeneratorProps) => {
    const { classes } = useStyles();
    const [displayName, setDisplayName] = useState("");
    const [files, setFiles] = useState<QueuedFile[]>([]);
    const [pasted, setPasted] = useState("");
    const [reading, setReading] = useState(false);
    // Index into files of the primary precedent; the first file unless the user picks another.
    const [primary, setPrimary] = useState(0);

    const key = toKey(displayName);
    const clash = key ? existing.find((p) => p.key === key) : undefined;
    const keyValid = isValidKey(key) && !clash;
    const hasInput = files.length > 0 || pasted.trim().length >= MIN_PASTED_LENGTH;
    const busy = starting || reading;

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        multiple: true,
        accept: { "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"] },
        onDrop: (accepted: File[]) =>
            setFiles((previous) => [
                ...previous,
                // The first file is usually the template the playbook is modelled on; the rest are context.
                ...accepted.map((file, i) => ({ file, role: (previous.length + i === 0 ? "template" : "guidance") as DocumentRole })),
            ]),
    });

    const remove = (index: number) => {
        setFiles((previous) => previous.filter((_, i) => i !== index));
        setPrimary((current) => (index < current ? current - 1 : index === current ? 0 : current));
    };

    const generate = async () => {
        setReading(true);
        try {
            const documents: GeneratorDocument[] = [];
            for (const queued of files) {
                documents.push({ name: queued.file.name, role: queued.role, documentBase64: await readFileBase64(queued.file) });
            }
            if (pasted.trim()) documents.push({ name: "Pasted text", role: "other", text: pasted.trim() });
            onGenerate({ key, displayName: displayName.trim(), documents, primaryDocument: primary });
        } finally {
            setReading(false);
        }
    };

    return (
        <Paper className={classes.root}>
            <Typography variant="h6" gutterBottom>
                New playbook
            </Typography>
            <TextField
                className={classes.field}
                label="Playbook name"
                placeholder="e.g. Settlement Agreement (E&W)"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                error={!!clash}
                helperText={
                    clash
                        ? clash.archived
                            ? `"${clash.displayName || clash.key}" is archived. Restore it from the list instead.`
                            : `"${clash.displayName || clash.key}" already exists. Open it from the list to add to it.`
                        : key && `Key: ${key}`
                }
                disabled={busy}
                fullWidth
            />

            <div {...getRootProps()} className={`${classes.zone} ${isDragActive ? classes.zoneActive : ""}`}>
                <input {...getInputProps()} />
                <UploadIcon />
                <Typography variant="body2">Drop Word documents here, or click to browse (.docx)</Typography>
            </div>
            {files.map((queued, index) => (
                <Paper key={`${queued.file.name}-${index}`} variant="outlined" className={classes.fileRow}>
                    <Typography variant="body2" className={classes.fileName}>
                        {queued.file.name}
                    </Typography>
                    {files.length > 1 && (
                        <FormControlLabel
                            label="Primary"
                            control={<Radio checked={primary === index} onChange={() => setPrimary(index)} />}
                        />
                    )}
                    <TextField
                        select
                        size="small"
                        label="Role"
                        style={{ width: 150 }}
                        value={queued.role}
                        onChange={(e) =>
                            setFiles((previous) =>
                                previous.map((entry, i) => (i === index ? { ...entry, role: e.target.value as DocumentRole } : entry)),
                            )
                        }
                    >
                        {ROLES.map((role) => (
                            <MenuItem key={role} value={role}>
                                {role}
                            </MenuItem>
                        ))}
                    </TextField>
                    <IconButton
                        aria-label={`Remove ${queued.file.name}`}
                        onClick={() => remove(index)}
                    >
                        <RemoveIcon />
                    </IconButton>
                </Paper>
            ))}

            <TextField
                style={{ marginTop: 16 }}
                label="Or paste text (added as an extra document)"
                value={pasted}
                onChange={(e) => setPasted(e.target.value)}
                multiline
                minRows={4}
                fullWidth
            />

            <Box className={classes.actions}>
                <Button
                    variant="contained"
                    startIcon={<GenerateIcon />}
                    onClick={generate}
                    disabled={!keyValid || !hasInput || busy}
                >
                    Generate playbook (AI)
                </Button>
                <Button onClick={() => onCreateEmpty(key, displayName.trim())} disabled={!keyValid || isCreating || busy}>
                    Create empty playbook instead
                </Button>
                <Button onClick={onCancel} disabled={starting}>
                    Cancel
                </Button>
            </Box>

            {starting && (
                <Box role="status" style={{ marginTop: 32 }}>
                    <LinearProgress />
                    <Typography variant="caption" style={{ color: "#777" }}>
                        Starting — the playbook opens as soon as it has been created.
                    </Typography>
                </Box>
            )}
        </Paper>
    );
};

export default PlaybookGenerator;
